import { useState } from "react";
import { Plus } from "lucide-react";
import { Topbar } from "@marekl/adminer/shell";
import {
  Page,
  Card,
  Button,
  Input,
  Textarea,
  Modal,
  FormStack,
  Alert,
} from "@marekl/adminer/components";
import { useAuth } from "../../auth/useAuth";
import { formatDateTime } from "@marekl/adminer/format";
import {
  useNoteList,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
  type Note,
  type NoteInput,
} from "../../lib/queries/notes";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; note: Note }
  | { kind: "delete"; note: Note };

export function NotesPage() {
  const { user } = useAuth();
  const { data, isLoading } = useNoteList();
  const notes = data?.items ?? [];
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const close = () => setModal({ kind: "closed" });

  const canEdit = (n: Note) =>
    !!user && (n.author_id === user.id || user.role === "superadmin");

  return (
    <>
      <Topbar crumbs={[{ label: "Poznámky" }]} />
      <Page.Content stack gap="lg">
        <Page.Header
          title="Poznámky"
          subtitle="týmová nástěnka"
          actions={
            <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
              <Plus size={16} aria-hidden />
              Nová poznámka
            </Button>
          }
        />

        {isLoading && <Card>Načítám…</Card>}
        {!isLoading && notes.length === 0 && (
          <Card>Žádné poznámky.</Card>
        )}

        {notes.map((n) => (
          <Card
            key={n.id}
            header={
              <Card.Header
                title={n.title}
                subtitle={`${n.author?.display_name ?? "—"} · ${formatDateTime(n.created_at)}`}
                subtitlePlacement="right"
                actions={
                  canEdit(n) && (
                    <>
                      <Button variant="ghost" onClick={() => setModal({ kind: "edit", note: n })}>
                        Upravit
                      </Button>
                      <Button variant="ghost" onClick={() => setModal({ kind: "delete", note: n })}>
                        Smazat
                      </Button>
                    </>
                  )
                }
              />
            }
          >
            <p style={{ whiteSpace: "pre-wrap" }}>{n.body}</p>
          </Card>
        ))}
      </Page.Content>

      {modal.kind === "create" && <NoteFormModal onClose={close} />}
      {modal.kind === "edit" && <NoteFormModal initial={modal.note} onClose={close} />}
      {modal.kind === "delete" && <DeleteNoteModal note={modal.note} onClose={close} />}
    </>
  );
}

function NoteFormModal({ initial, onClose }: { initial?: Note; onClose: () => void }) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [error, setError] = useState<string | null>(null);
  const create = useCreateNote();
  const update = useUpdateNote();
  const isEdit = !!initial;
  const isPending = create.isPending || update.isPending;

  async function submit() {
    setError(null);
    const input: NoteInput = { title, body };
    try {
      if (isEdit) {
        await update.mutateAsync({ id: initial!.id, ...input });
      } else {
        await create.mutateAsync(input);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Uložení se nezdařilo");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Upravit poznámku" : "Nová poznámka"}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Zrušit</Button>
          <Button variant="primary" onClick={submit} disabled={isPending || !title.trim()}>
            Uložit
          </Button>
        </>
      }
    >
      <FormStack>
        {error && <Alert variant="err">{error}</Alert>}
        <Input
          label="Titulek"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <Textarea
          label="Text"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
        />
      </FormStack>
    </Modal>
  );
}

function DeleteNoteModal({ note, onClose }: { note: Note; onClose: () => void }) {
  const del = useDeleteNote();
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      await del.mutateAsync(note.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Smazání se nezdařilo");
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Smazat poznámku"
      footer={
        <>
          <Button variant="default" onClick={onClose}>Zrušit</Button>
          <Button variant="danger" onClick={submit} disabled={del.isPending}>
            Smazat
          </Button>
        </>
      }
    >
      <FormStack>
        {error && <Alert variant="err">{error}</Alert>}
        <p>Opravdu chcete smazat poznámku <strong>{note.title}</strong>?</p>
      </FormStack>
    </Modal>
  );
}
